import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Archive,
  ArrowDown,
  ArrowUp,
  Bell,
  CheckCircle2,
  Edit3,
  Eye,
  EyeOff,
  LoaderCircle,
  Plus,
  Search,
  Send,
  Trash2,
} from 'lucide-react';
import { Navigate, useParams } from 'react-router-dom';
import FormRenderer from '../components/FormRenderer';
import { ConfirmDialog, EmptyState, Modal, Notice, PageLoader, StatusBadge, formatTimestamp } from '../components/UI';
import {
  MODULES,
  PUSH_SUPPORTED_MODULES,
  generateRecordNotificationPreview,
  recordSubtitle,
  recordTitle,
  translatedValue,
} from '../config/modules';
import { useAuth } from '../context/AuthContext';
import {
  getNextAvailableDocumentId,
  getNextSequentialDocumentId,
  permanentlyDeleteRecord,
  reorderRecords,
  saveRecord,
  subscribeToCollection,
  updateRecord,
} from '../services/data';
import { sendRecordNotification } from '../services/adminApi';

function recordWantsPush(record, existing, moduleKey) {
  if (!record || record.isActive === false) return false;
  if (!PUSH_SUPPORTED_MODULES.includes(moduleKey)) return false;
  if (existing) return record.sendUpdateNotification === true;
  return (moduleKey === 'announcements' && record.priority === 'emergency') || record.sendPush === true;
}

export default function CollectionPage() {
  const { moduleKey } = useParams();
  const module = MODULES[moduleKey];
  const { can } = useAuth();
  const formRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [creatingId, setCreatingId] = useState('');
  const [busy, setBusy] = useState(false);
  const [formLocked, setFormLocked] = useState(false);
  const [confirm, setConfirm] = useState(null);

  // Push Notification dialog state
  const [pushTargetRecord, setPushTargetRecord] = useState(null);
  const [pushAudience, setPushAudience] = useState('all');
  const [pushSubscriptionIds, setPushSubscriptionIds] = useState('');
  const [pushCustomTitle, setPushCustomTitle] = useState('');
  const [pushCustomBody, setPushCustomBody] = useState('');
  const [pushBusy, setPushBusy] = useState(false);
  const [pushModalError, setPushModalError] = useState('');
  const [pushModalSuccess, setPushModalSuccess] = useState('');

  useEffect(() => {
    if (!module || !can(moduleKey, 'view')) return undefined;
    setLoading(true);
    return subscribeToCollection(module.collection, (records) => {
      setRows(records);
      setLoading(false);
    }, (snapshotError) => {
      setError(snapshotError.message || 'Data load thai shakyo nahi.');
      setLoading(false);
    });
  }, [can, module, moduleKey]);

  const filteredRows = useMemo(() => rows.filter((record) => {
    const active = record.isActive !== false;
    if (statusFilter === 'active' && !active) return false;
    if (statusFilter === 'inactive' && active) return false;
    const haystack = JSON.stringify(record).toLocaleLowerCase('en-IN');
    return haystack.includes(search.trim().toLocaleLowerCase('en-IN'));
  }), [rows, search, statusFilter]);

  if (!module) return <Navigate to="/" replace />;
  if (!can(moduleKey, 'view')) return <Navigate to="/unauthorized" replace />;

  const finishEditor = () => {
    setCreating(false);
    setCreatingId('');
    setEditing(null);
    setFormLocked(false);
  };

  const requestCloseEditor = () => {
    if (busy || formLocked) return;
    if (formRef.current?.cancel) formRef.current.cancel();
    else finishEditor();
  };

  const openCreate = () => {
    if (loading) return;
    setError('');
    setSuccess('');
    setCreatingId(getNextSequentialDocumentId(rows, module.idPrefix));
    setCreating(true);
  };

  const openPushDialog = (record) => {
    const preview = generateRecordNotificationPreview(record, moduleKey);
    setPushTargetRecord(record);
    setPushCustomTitle(preview.title || '');
    setPushCustomBody(preview.body || '');
    setPushAudience('all');
    setPushSubscriptionIds('');
    setPushModalError('');
    setPushModalSuccess('');
  };

  const handleSendPushModal = async (event) => {
    event.preventDefault();
    if (!pushTargetRecord) return;

    setPushBusy(true);
    setPushModalError('');
    setPushModalSuccess('');

    try {
      const subscriptionIds = pushAudience === 'subscription_ids'
        ? pushSubscriptionIds.split(',').map((item) => item.trim()).filter(Boolean)
        : [];

      const result = await sendRecordNotification({
        moduleKey,
        collection: module.collection,
        documentId: pushTargetRecord.id,
        title: pushCustomTitle,
        body: pushCustomBody,
        isUpdate: false,
        targetAudience: pushAudience,
        subscriptionIds,
      });

      setPushModalSuccess(
        `Push notification successfully sent to ${result.recipients || 0} subscriber(s)! (OneSignal ID: ${result.notificationId})`,
      );
      setSuccess(`Push notification sent for "${recordTitle(pushTargetRecord, moduleKey)}" to ${result.recipients || 0} subscriber(s).`);
    } catch (pushErr) {
      setPushModalError(pushErr.message || 'Push notification sending failed.');
    } finally {
      setPushBusy(false);
    }
  };

  const save = async (record) => {
    const existing = Boolean(editing);
    const isAnnouncement = moduleKey === 'announcements';
    const isPushSupported = PUSH_SUPPORTED_MODULES.includes(moduleKey);
    const shouldNotify = isPushSupported && recordWantsPush(record, existing, moduleKey);

    if (shouldNotify && !can(moduleKey, 'notify')) {
      throw new Error('Push notification mokalva mate Notify permission aapel nathi.');
    }

    setBusy(true);
    setError('');
    try {
      const wasActive = editing?.isActive !== false;
      const willBeActive = record.isActive !== false;
      if (existing && wasActive !== willBeActive && !can(moduleKey, willBeActive ? 'publish' : 'archive')) {
        throw new Error(`${willBeActive ? 'Publish' : 'Archive'} permission aapel nathi.`);
      }

      const recordToSave = isAnnouncement && willBeActive && !record.publishedAt
        ? { ...record, publishedAt: new Date().toISOString() }
        : record;
      const id = await saveRecord(module.collection, recordToSave, existing);

      let notificationResult = null;
      if (shouldNotify) {
        try {
          notificationResult = await sendRecordNotification({
            moduleKey,
            collection: module.collection,
            documentId: id,
            title: record.notificationTitle,
            body: record.notificationText,
            isUpdate: existing,
          });
          await updateRecord(module.collection, id, {
            sendUpdateNotification: false,
            sendPush: false,
          });
        } catch (notificationError) {
          setError(`${module.singular} save thai gayu, pan push notification send nathi thayu: ${notificationError.message || 'Unknown notification error.'}`);
        }
      }

      const baseSuccess = `${module.singular} successfully ${existing ? 'updated' : 'created'}.`;
      if (notificationResult?.success) {
        setSuccess(`${baseSuccess} Push notification OneSignal dwara ${notificationResult.recipients || 0} users ne send thai.`);
      } else {
        setSuccess(baseSuccess);
      }
      return { id };
    } catch (saveError) {
      if (!existing && saveError.code === 'already-exists') {
        try {
          const refreshedId = await getNextAvailableDocumentId(module.collection, module.idPrefix);
          setCreatingId(refreshedId);
          const message = `${saveError.message} Navo ID ${refreshedId} automatically generate thayo chhe; fari Save karo.`;
          setError(message);
          throw new Error(message);
        } catch (refreshError) {
          if (refreshError.message?.includes('automatically generate')) throw refreshError;
          const message = saveError.message || 'Generated ID already use ma chhe. Form close kari fari Add karo.';
          setError(message);
          throw new Error(message);
        }
      }
      setError(saveError.message || 'Save failed.');
      throw saveError;
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (record) => {
    const nextActive = record.isActive === false;
    if (!can(moduleKey, nextActive ? 'publish' : 'archive')) return;

    const isAnnouncement = moduleKey === 'announcements';
    const isPushSupported = PUSH_SUPPORTED_MODULES.includes(moduleKey);
    const shouldNotify = isPushSupported
      && nextActive
      && ((moduleKey === 'announcements' && record.priority === 'emergency') || record.sendPush === true);

    if (shouldNotify && !can(moduleKey, 'notify')) {
      setError(`Aa ${module.singular.toLowerCase()} publish karta push mokalvani chhe, pan Notify permission aapel nathi.`);
      return;
    }

    setBusy(true);
    setError('');
    try {
      const changes = { isActive: nextActive };
      if (isAnnouncement && nextActive && !record.publishedAt) changes.publishedAt = new Date().toISOString();
      await updateRecord(module.collection, record.id, changes);

      if (shouldNotify) {
        try {
          const notifyResult = await sendRecordNotification({
            moduleKey,
            collection: module.collection,
            documentId: record.id,
            title: record.notificationTitle,
            body: record.notificationText,
            isUpdate: false,
          });
          await updateRecord(module.collection, record.id, { sendPush: false, sendUpdateNotification: false });
          setSuccess(`${recordTitle(record, moduleKey)} published ane push notification ${notifyResult.recipients || 0} subscribers ne send thai.`);
        } catch (notificationError) {
          setSuccess(`${recordTitle(record, moduleKey)} published.`);
          setError(`Publish thai gayu, pan push notification send nathi thayu: ${notificationError.message || 'Unknown notification error.'}`);
        }
      } else {
        setSuccess(`${recordTitle(record, moduleKey)} ${nextActive ? 'published' : 'archived'}.`);
      }
    } catch (toggleError) {
      setError(toggleError.message || 'Status update failed.');
    } finally { setBusy(false); }
  };

  const move = async (record, direction) => {
    const index = rows.findIndex((item) => item.id === record.id);
    const target = index + direction;
    if (target < 0 || target >= rows.length) return;
    const nextRows = [...rows];
    [nextRows[index], nextRows[target]] = [nextRows[target], nextRows[index]];
    setRows(nextRows);
    try { await reorderRecords(module.collection, nextRows); }
    catch (moveError) { setError(moveError.message || 'Reorder failed.'); }
  };

  const permanentDelete = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      await permanentlyDeleteRecord(module.collection, confirm.id, confirm);
      setSuccess(`${recordTitle(confirm, moduleKey)} permanently deleted. Linked Cloudinary images cleanup ma mukai gai chhe.`);
      setConfirm(null);
    } catch (deleteError) { setError(deleteError.message || 'Delete failed.'); }
    finally { setBusy(false); }
  };

  const isAnnouncementsModule = moduleKey === 'announcements';

  return (
    <div className="page-stack">
      <header className="page-heading">
        <div><p className="eyebrow">Content module</p><h1>{module.label}</h1><p>{rows.length} total records · {rows.filter((row) => row.isActive !== false).length} active</p></div>
        {can(moduleKey, 'create') ? <button className="button primary" onClick={openCreate} disabled={loading}><Plus size={18} /> Add {module.singular}</button> : null}
      </header>

      {error ? <Notice type="error" onClose={() => setError('')}>{error}</Notice> : null}
      {success ? <Notice type="success" onClose={() => setSuccess('')}>{success}</Notice> : null}

      <section className="panel-card table-panel">
        <div className="table-toolbar">
          <label className="search-box"><Search size={18} /><input value={search} placeholder={`Search ${module.label.toLowerCase()}…`} onChange={(event) => setSearch(event.target.value)} /></label>
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            <option value="all">All status</option><option value="active">Active</option><option value="inactive">Inactive</option>
          </select>
        </div>

        {loading ? <PageLoader /> : filteredRows.length ? (
          <div className="data-table-wrap">
            <table className="data-table">
              <thead><tr><th>Content</th><th>Status</th><th>Updated</th><th>Order</th><th className="actions-column">Actions</th></tr></thead>
              <tbody>{filteredRows.map((record, index) => (
                <tr key={record.id}>
                  <td><div className="content-cell">
                    {(record.image || record.cover || record.photo) ? <img src={record.image || record.cover || record.photo} alt="" /> : <span className="content-placeholder" style={{ color: module.color }}><module.icon size={20} /></span>}
                    <div>
                      <strong>{recordTitle(record, moduleKey)}</strong>
                      <span>{recordSubtitle(record, moduleKey) || record.id}</span>
                      <code>{record.id}</code>
                      {record.notificationSentAt ? (
                        <small className="notification-meta" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#059669', fontSize: 11 }}>
                          <CheckCircle2 size={12} /> Push sent ({formatTimestamp(record.notificationSentAt)})
                        </small>
                      ) : null}
                    </div>
                  </div></td>
                  <td><StatusBadge active={record.isActive !== false} /></td>
                  <td className="muted-cell">{formatTimestamp(record.updatedAt)}</td>
                  <td><div className="order-actions"><span>{record.sortOrder ?? index + 1}</span>{can(moduleKey, 'reorder') ? <><button onClick={() => move(record, -1)} disabled={index === 0}><ArrowUp size={15} /></button><button onClick={() => move(record, 1)} disabled={index === rows.length - 1}><ArrowDown size={15} /></button></> : null}</div></td>
                  <td><div className="row-actions">
                    {PUSH_SUPPORTED_MODULES.includes(moduleKey) && (can(moduleKey, 'notify') || can(moduleKey, 'edit') || can(moduleKey, 'publish')) ? (
                      <button className="notify-action-button" title={`Send Push Notification for this ${module.singular}`} onClick={() => openPushDialog(record)}>
                        <Bell size={17} />
                      </button>
                    ) : null}
                    {can(moduleKey, 'edit') ? <button title="Edit" onClick={() => setEditing(record)}><Edit3 size={17} /></button> : null}
                    {can(moduleKey, record.isActive === false ? 'publish' : 'archive') ? <button title={record.isActive === false ? 'Publish' : 'Archive'} onClick={() => toggleActive(record)}>{record.isActive === false ? <Eye size={17} /> : <EyeOff size={17} />}</button> : null}
                    {can(moduleKey, 'delete') ? <button className="danger-ghost" title="Permanent delete" onClick={() => setConfirm(record)}><Trash2 size={17} /></button> : null}
                  </div></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        ) : <EmptyState icon={Archive} title="No records found" description="Search/filter change karo athva navo record add karo." />}
      </section>

      <Modal
        open={creating || Boolean(editing)}
        title={`${creating ? 'Add' : 'Edit'} ${module.singular}`}
        subtitle="Gujarati, Hindi ane English content carefully fill karo."
        onClose={requestCloseEditor}
        closeDisabled={busy || formLocked}
      >
        <FormRenderer
          ref={formRef}
          key={editing?.id || (creating ? 'new' : 'closed')}
          module={module}
          initialValue={editing}
          generatedDocumentId={creating ? creatingId : ''}
          isNew={creating}
          busy={busy}
          canUpload={can(moduleKey, 'media')}
          onInteractionLockChange={setFormLocked}
          onSubmit={save}
          onSaved={finishEditor}
          onCancel={finishEditor}
          categoryRecords={rows}
          canDeleteCategory={can(moduleKey, 'delete')}
        />
      </Modal>

      {/* Push Notification Dialog */}
      <Modal
        open={Boolean(pushTargetRecord)}
        title={`Send Push Notification`}
        subtitle={`Broadcast ${module.singular.toLowerCase()} "${pushTargetRecord ? recordTitle(pushTargetRecord, moduleKey) : ''}" to mobile devices.`}
        onClose={() => { if (!pushBusy) setPushTargetRecord(null); }}
        closeDisabled={pushBusy}
        size="small"
      >
        {pushTargetRecord ? (
          <form className="record-form" onSubmit={handleSendPushModal}>
            {pushModalError ? <Notice type="error">{pushModalError}</Notice> : null}
            {pushModalSuccess ? <Notice type="success">{pushModalSuccess}</Notice> : null}
            {pushTargetRecord.isActive === false ? (
              <Notice type="info">Note: Aa {module.singular.toLowerCase()} hal Inactive chhe. Push Notification mokalvathi mobile app ma aa record automatically publish thai jase.</Notice>
            ) : null}

            <label className="field">
              <span>Notification Title<em>*</em></span>
              <input
                value={pushCustomTitle}
                onChange={(e) => setPushCustomTitle(e.target.value)}
                placeholder="Enter notification title…"
                required
              />
            </label>

            <label className="field">
              <span>Notification Message<em>*</em></span>
              <textarea
                rows={3}
                value={pushCustomBody}
                onChange={(e) => setPushCustomBody(e.target.value)}
                placeholder="Enter notification message…"
                required
              />
            </label>

            {(pushTargetRecord.image || pushTargetRecord.cover || pushTargetRecord.photo) ? (
              <small style={{ display: 'block', color: '#6b7280', marginBottom: 8 }}>
                Image attached: Yes (will display as push banner on Android/iOS)
              </small>
            ) : null}

            <label className="field">
              <span>Target Audience</span>
              <select value={pushAudience} onChange={(e) => setPushAudience(e.target.value)}>
                <option value="all">All Subscribed Users (Broadcast)</option>
                <option value="subscription_ids">Specific Subscription ID (Testing)</option>
              </select>
            </label>

            {pushAudience === 'subscription_ids' ? (
              <label className="field">
                <span>OneSignal Subscription / Player ID<em>*</em></span>
                <input
                  value={pushSubscriptionIds}
                  placeholder="e.g. 7c30a845-6677-48f5-9ab7-..."
                  onChange={(e) => setPushSubscriptionIds(e.target.value)}
                  required
                />
                <small>OneSignal Subscription ID paste karo test delivery mate.</small>
              </label>
            ) : null}

            <div className="form-actions" style={{ marginTop: 16 }}>
              <button
                className="button secondary"
                type="button"
                onClick={() => setPushTargetRecord(null)}
                disabled={pushBusy}
              >
                Close
              </button>
              <button className="button primary" disabled={pushBusy}>
                {pushBusy ? <LoaderCircle className="spin" size={17} /> : <Send size={17} />}
                {pushBusy ? 'Sending Notification…' : 'Send Push Notification'}
              </button>
            </div>
          </form>
        ) : null}
      </Modal>

      <ConfirmDialog open={Boolean(confirm)} title={`Delete ${module.singular}?`} description="Aa permanent delete chhe. Record sathe linked managed Cloudinary images pan cleanup thase ane undo nahi thai shake." confirmLabel="Permanently delete" danger busy={busy} onClose={() => setConfirm(null)} onConfirm={permanentDelete} />
    </div>
  );
}

